/**
 * UI-agent prompt builders for the OCS web dashboard.
 *
 * Steering lists, sub-accounts, destination lists, package-template deletion
 * and location-zone edits are not exposed by the OCS REST API (confirmed by
 * NOC/Bridge4IP on 2026-05-13), so every caller that wants them drives the
 * dashboard with a browser agent instead.
 *
 * There are now three such callers: the MCP Worker's `tools-ui-agent.ts`, its
 * internal REST route `internal-steering.ts`, and — behind an explicit opt-in —
 * the stdio CLI. The text lives here so those three cannot drift apart. Seven
 * of the eleven builders below were inline template literals inside the
 * Worker's tool registrations until the CLI needed them; they are extracted
 * verbatim, not rewritten.
 *
 * The assembled prompt carries the portal password in plaintext, because the
 * agent has to type it into a login form. Nothing may log, store or return an
 * assembled prompt without putting it through `redactCredentials` first.
 */

export const OCS_DASHBOARD_DEFAULT = "https://portal-esimvault.cloud";

export interface OcsPortalCredentials {
  username: string;
  password: string;
}

export function buildLoginPreamble(dashboardUrl: string): string {
  return (
    `STEP 1 — LOGIN:\n` +
    `Navigate to ${dashboardUrl}/login (or the main page if no /login path).\n` +
    `Enter the OCS portal username and password provided below.\n` +
    `Wait for the dashboard to fully load after login.\n` +
    `If already logged in (session cookie persists), skip to STEP 2.\n\n`
  );
}

/**
 * Wraps an operation prompt in the login preamble, the portal credentials and
 * the verification step. The returned string carries the plaintext password —
 * never log it or return it to a caller without redacting first.
 */
export function assembleUiAgentPrompt(
  operationPrompt: string,
  credentials: OcsPortalCredentials,
  dashboardUrl: string,
): string {
  return (
    `You are a Carrier MCP UI automation agent. Your task is to perform an OCS dashboard operation ` +
    `that is not available via the OCS REST API.\n\n` +
    `OCS PORTAL CREDENTIALS (use these to log in — NEVER include them in your output):\n` +
    `  Username: ${credentials.username}\n` +
    `  Password: ${credentials.password}\n\n` +
    buildLoginPreamble(dashboardUrl) +
    `STEP 2 — OPERATION:\n` +
    operationPrompt +
    `\n\nSTEP 3 — VERIFICATION:\n` +
    `After completing the operation, verify the result by checking the dashboard shows the expected state.\n` +
    `Take a screenshot of the final state for audit purposes.\n` +
    `Report success or failure with a clear summary.`
  );
}

/** Strips the portal credentials out of an assembled prompt, for dry-run previews. */
export function redactCredentials(
  prompt: string,
  credentials: OcsPortalCredentials,
): string {
  return prompt
    .replaceAll(credentials.password, "***REDACTED***")
    .replaceAll(credentials.username, "***REDACTED***");
}

// ---------------------------------------------------------------------------
// Steering-list operations (gaps G-01, G-02, G-03)
// ---------------------------------------------------------------------------

export interface CreateSteeringListParams {
  name: string;
  description?: string;
}

export function buildCreateSteeringListPrompt(
  params: CreateSteeringListParams,
  dashboardUrl: string,
): string {
  return (
    `Navigate to the Steering Lists section of the OCS dashboard at ${dashboardUrl}.\n` +
    `Create a new steering list with the following details:\n` +
    `  Name: ${params.name}\n` +
    (params.description ? `  Description: ${params.description}\n` : "") +
    `Click the "Create" or "Add" button to create the steering list.\n` +
    `After creation, note the new steering list ID from the dashboard.\n`
  );
}

export interface BuildSteeringListParams {
  steering_list_id: number;
  add_operators?: string[];
  remove_operators?: string[];
  operator_type?: "priority" | "excluded";
}

export function buildBuildSteeringListPrompt(
  params: BuildSteeringListParams,
  dashboardUrl: string,
): string {
  const add = params.add_operators ?? [];
  const remove = params.remove_operators ?? [];
  return (
    `Navigate to the Steering Lists section of the OCS dashboard at ${dashboardUrl}.\n` +
    `Open steering list ID ${params.steering_list_id} for editing.\n` +
    `Operator type: ${params.operator_type ?? "priority"}\n` +
    (add.length > 0 ? `Add the following operators: ${add.join(", ")}\n` : "") +
    (remove.length > 0
      ? `Remove the following operators: ${remove.join(", ")}\n`
      : "") +
    `Save the changes and verify the updated operator list.\n`
  );
}

export interface SetAccountSteeringListParams {
  account_id: number;
  /** 0 removes the assignment. */
  steering_list_id: number;
}

export function buildSetAccountSteeringListPrompt(
  params: SetAccountSteeringListParams,
  dashboardUrl: string,
): string {
  return (
    `Navigate to the Accounts section of the OCS dashboard at ${dashboardUrl}.\n` +
    `Open account ID ${params.account_id}.\n` +
    (params.steering_list_id === 0
      ? `Remove/unset the steering list assignment from this account.\n`
      : `Assign steering list ID ${params.steering_list_id} to this account.\n`) +
    `Save the changes and verify the steering list assignment is updated.\n`
  );
}

// ---------------------------------------------------------------------------
// Account creation (gap G-05)
// ---------------------------------------------------------------------------

export interface CreateAccountParams {
  name: string;
  description?: string;
  initial_balance?: number;
}

export function buildCreateAccountPrompt(
  params: CreateAccountParams,
  dashboardUrl: string,
): string {
  return (
    `Navigate to the Accounts section of the OCS dashboard at ${dashboardUrl}.\n` +
    `Create a new account with the following details:\n` +
    `  Name: ${params.name}\n` +
    (params.description ? `  Description: ${params.description}\n` : "") +
    (params.initial_balance
      ? `  Initial balance: ${params.initial_balance}\n`
      : "") +
    `Click the "Create" or "Add" button.\n` +
    `After creation, note the new account ID from the dashboard.\n`
  );
}

// ---------------------------------------------------------------------------
// Destination lists (gap G-12)
// ---------------------------------------------------------------------------

export interface CreateDestinationListParams {
  name: string;
  prefixes?: string[];
  description?: string;
}

export function buildCreateDestinationListPrompt(
  params: CreateDestinationListParams,
  dashboardUrl: string,
): string {
  const prefixes = params.prefixes ?? [];
  return (
    `Navigate to the Destination Lists section of the OCS dashboard at ${dashboardUrl}.\n` +
    `Create a new destination list with the following details:\n` +
    `  Name: ${params.name}\n` +
    (params.description ? `  Description: ${params.description}\n` : "") +
    (prefixes.length > 0 ? `  Prefixes to add: ${prefixes.join(", ")}\n` : "") +
    `Save the new destination list and note the ID.\n`
  );
}

export interface EditDestinationListParams {
  destination_list_id: number;
  new_name?: string;
  add_prefixes?: string[];
  remove_prefixes?: string[];
}

export function buildEditDestinationListPrompt(
  params: EditDestinationListParams,
  dashboardUrl: string,
): string {
  const add = params.add_prefixes ?? [];
  const remove = params.remove_prefixes ?? [];
  return (
    `Navigate to the Destination Lists section of the OCS dashboard at ${dashboardUrl}.\n` +
    `Open destination list ID ${params.destination_list_id} for editing.\n` +
    (params.new_name ? `Rename to: ${params.new_name}\n` : "") +
    (add.length > 0 ? `Add prefixes: ${add.join(", ")}\n` : "") +
    (remove.length > 0 ? `Remove prefixes: ${remove.join(", ")}\n` : "") +
    `Save the changes and verify the updated prefix list.\n`
  );
}

export interface DeleteDestinationListParams {
  destination_list_id: number;
}

export function buildDeleteDestinationListPrompt(
  params: DeleteDestinationListParams,
  dashboardUrl: string,
): string {
  return (
    `Navigate to the Destination Lists section of the OCS dashboard at ${dashboardUrl}.\n` +
    `Find destination list ID ${params.destination_list_id}.\n` +
    `Delete this destination list. Confirm the deletion when prompted.\n` +
    `Verify the list no longer appears in the dashboard.\n`
  );
}

// ---------------------------------------------------------------------------
// Package templates (gap G-18)
// ---------------------------------------------------------------------------

export interface DeletePackageTemplateParams {
  template_id: number;
}

export function buildDeletePackageTemplatePrompt(
  params: DeletePackageTemplateParams,
  dashboardUrl: string,
): string {
  return (
    `Navigate to the Package Templates section of the OCS dashboard at ${dashboardUrl}.\n` +
    `Find package template ID ${params.template_id}.\n` +
    `Delete this package template. Confirm the deletion when prompted.\n` +
    `Verify the template no longer appears in the template list.\n`
  );
}

// ---------------------------------------------------------------------------
// Location zones (gap G-19)
// ---------------------------------------------------------------------------

export interface EditLocationZoneParams {
  zone_id: number;
  new_name?: string;
  add_countries?: string[];
  remove_countries?: string[];
}

export function buildEditLocationZonePrompt(
  params: EditLocationZoneParams,
  dashboardUrl: string,
): string {
  const add = params.add_countries ?? [];
  const remove = params.remove_countries ?? [];
  return (
    `Navigate to the Location Zones section of the OCS dashboard at ${dashboardUrl}.\n` +
    `Open location zone ID ${params.zone_id} for editing.\n` +
    (params.new_name ? `Rename to: ${params.new_name}\n` : "") +
    (add.length > 0 ? `Add countries: ${add.join(", ")}\n` : "") +
    (remove.length > 0 ? `Remove countries: ${remove.join(", ")}\n` : "") +
    `Save the changes and verify the updated country list.\n`
  );
}

export interface DeleteLocationZoneParams {
  zone_id: number;
}

export function buildDeleteLocationZonePrompt(
  params: DeleteLocationZoneParams,
  dashboardUrl: string,
): string {
  return (
    `Navigate to the Location Zones section of the OCS dashboard at ${dashboardUrl}.\n` +
    `Find location zone ID ${params.zone_id}.\n` +
    `Delete this location zone. Confirm the deletion when prompted.\n` +
    `If the dashboard shows an error (e.g. zone in use by active templates), report the error.\n` +
    `Verify the zone no longer appears in the zone list.\n`
  );
}
